import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, NavBar, Toast } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import schools from '../../db/schools.json';
import { localizeSchool } from '../../db/localizeSchool';
import { useT } from '../../i18n';
import './styles.css';

const STATUS_KEYS = ['submitted', 'confirmed', 'not_found', 'mismatch', 'unverified', 'confirmation_error'];

const formatDate = (value) => {
	if (!value) return '-';
	try {
		return new Date(value).toLocaleString();
	} catch {
		return value;
	}
};

const getInitData = () => {
	if (typeof window === 'undefined') return '';
	return window.Telegram?.WebApp?.initData || '';
};

const TOKEN_STORAGE_KEY = 'visDonate.adminToken';

const readStoredToken = () => {
	if (typeof window === 'undefined') return '';
	try {
		return window.sessionStorage?.getItem(TOKEN_STORAGE_KEY) || '';
	} catch {
		return '';
	}
};

const persistToken = (value) => {
	if (typeof window === 'undefined') return;
	try {
		if (value) window.sessionStorage?.setItem(TOKEN_STORAGE_KEY, value);
		else window.sessionStorage?.removeItem(TOKEN_STORAGE_KEY);
	} catch {
		// ignore (e.g., Safari private mode)
	}
};

const removeTokenFromUrl = () => {
	if (typeof window === 'undefined') return;
	const url = new URL(window.location.href);
	const hadAdminAuth =
		url.searchParams.has('admin_user_id') ||
		url.searchParams.has('admin_auth_date') ||
		url.searchParams.has('admin_auth_hash');
	if (!url.searchParams.has('token') && !hadAdminAuth) return;
	url.searchParams.delete('token');
	url.searchParams.delete('admin_user_id');
	url.searchParams.delete('admin_auth_date');
	url.searchParams.delete('admin_auth_hash');
	const nextUrl = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState(window.history.state, '', nextUrl);
};

const readAdminLinkAuth = () => {
	if (typeof window === 'undefined') return null;
	const params = new URLSearchParams(window.location.search);
	const userId = params.get('admin_user_id');
	const authDate = params.get('admin_auth_date');
	const hash = params.get('admin_auth_hash');
    return userId && authDate && hash ? { authDate, hash, userId } : null;
};

const buildAdminAuthHeaders = ({ adminLinkAuth, initData, token }) => {
    const headers = {};
    if (initData) headers['x-telegram-init-data'] = initData;
    if (adminLinkAuth) {
        headers['x-admin-telegram-user-id'] = adminLinkAuth.userId;
        headers['x-admin-auth-date'] = adminLinkAuth.authDate;
        headers['x-admin-auth-hash'] = adminLinkAuth.hash;
    }
    const tokenValue = token.trim();
    if (tokenValue) headers.authorization = `Bearer ${tokenValue}`;
    return Object.keys(headers).length ? headers : null;
};

const fetchDonationRecords = (headers) =>
    fetch('/api/admin/donations', { headers });

const postDonationConfirmation = (headers, key) =>
    fetch('/api/admin/donations/confirm', {
        method: 'POST',
        headers: { ...headers, 'content-type': 'application/json' },
        body: JSON.stringify(key ? { key } : {}),
    });

const Admin = () => {
    const navigate = useNavigate();
	const { locale, t } = useT();
	const initData = getInitData();
	const [urlToken, setUrlToken] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
	const [token, setToken] = useState(() => {
		const urlToken = new URLSearchParams(window.location.search).get('token');
		return urlToken || readStoredToken();
	});
	const [adminLinkAuth] = useState(readAdminLinkAuth);
	const [records, setRecords] = useState([]);
	const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [needsToken, setNeedsToken] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [query, setQuery] = useState('');
    const didInitialLoad = useRef(false);
	const localizedSchools = schools.map((school) => localizeSchool(school, locale));
	const statusLabel = {
		submitted: t('admin_status_submitted'),
		confirmed: t('admin_status_confirmed'),
		not_found: t('admin_status_not_found'),
		mismatch: t('admin_status_mismatch'),
		unverified: t('admin_status_unverified'),
		confirmation_error: t('admin_status_check_failed'),
	};
	const getSchoolName = (record) =>
		localizedSchools.find((school) => school.id === record.schoolId)?.name || record.schoolName || record.schoolId;

    const buildAuthHeaders = useCallback((overrideToken) =>
        buildAdminAuthHeaders({
            adminLinkAuth,
            initData,
            token: overrideToken ?? token,
        }), [adminLinkAuth, initData, token]);

    const loadRecords = useCallback(async (overrideToken) => {
        const headers = buildAuthHeaders(overrideToken);
        if (!headers) {
            Toast.show({ icon: 'fail', content: t('admin_token_required') });
			return;
        }
        try {
            setLoading(true);
            const response = await fetchDonationRecords(headers);
            if (response.status === 401) {
				if (initData) {
					setNeedsToken(true);
					throw new Error(t('admin_token_required'));
				}
				persistToken('');
				throw new Error(t('admin_unauthorized'));
			}
			if (!response.ok) throw new Error(t('admin_load_failed'));
			const body = await response.json();
			setRecords(body.records || []);
			if (initData) {
				setNeedsToken(false);
				if ((overrideToken ?? token).trim()) setToken('');
			} else {
				const used = (overrideToken ?? token).trim();
				if (used) persistToken(used);
			}
		} catch (err) {
			Toast.show({ icon: 'fail', content: err.message || t('admin_load_failed') });
        } finally {
            setLoading(false);
        }
    }, [buildAuthHeaders, initData, t, token]);

    const confirmRecords = useCallback(async (key) => {
        const headers = buildAuthHeaders();
        if (!headers) {
			Toast.show({ icon: 'fail', content: t('admin_token_required') });
			return;
        }
        try {
            setChecking(true);
            const response = await postDonationConfirmation(headers, key);
            if (response.status === 401 && initData) setNeedsToken(true);
			if (!response.ok) throw new Error(t('admin_check_failed'));
			const body = await response.json();
			Toast.show({ content: t('admin_checked_count', { count: body.updated?.length || 0 }) });
			await loadRecords();
		} catch (err) {
			Toast.show({ icon: 'fail', content: err.message || t('admin_check_failed') });
        } finally {
            setChecking(false);
        }
    }, [buildAuthHeaders, initData, loadRecords, t]);

	const copyValue = async (value) => {
		if (!value || !navigator.clipboard) return;
		await navigator.clipboard.writeText(value);
		Toast.show({ content: t('admin_copied') });
	};

	const normalizedQuery = query.trim().toLowerCase();
	const filteredRecords = records.filter((record) => {
		const status = record.status || 'submitted';
		if (statusFilter !== 'all' && status !== statusFilter) return false;
		if (!normalizedQuery) return true;
		return [
			getSchoolName(record),
			record.schoolName,
			record.schoolId,
			record.walletAddress,
			record.signedBocHash,
			record.confirmation?.transactionHash,
		]
			.filter(Boolean)
			.some((value) => String(value).toLowerCase().includes(normalizedQuery));
	});

    useEffect(() => {
        if (didInitialLoad.current) return;
        didInitialLoad.current = true;
        if (urlToken) {
            removeTokenFromUrl();
            setUrlToken('');
        }
        if (initData || token) loadRecords(token);
    }, [initData, loadRecords, token, urlToken]);

	return (
		<div className="admin">
			<NavBar onBack={() => navigate('/')}>{t('admin_title')}</NavBar>
			<div className="admin_toolbar">
				{(!initData || needsToken) && (
					<Input
						className="admin_token"
						placeholder={t('admin_token_placeholder')}
						type="password"
						value={token}
						onChange={setToken}
					/>
				)}
				<div className="admin_actions">
					<Button size="small" color="primary" loading={loading} onClick={() => loadRecords()}>
						{t('admin_load')}
					</Button>
					<Button size="small" loading={checking} onClick={() => confirmRecords()}>
						{t('admin_check_pending')}
					</Button>
				</div>
			</div>
			<div className="admin_filters">
				<Input
					className="admin_search"
					placeholder={t('admin_search_placeholder')}
					value={query}
					onChange={setQuery}
				/>
				<select
					className="admin_status_filter"
					value={statusFilter}
					onChange={(event) => setStatusFilter(event.target.value)}
				>
					<option value="all">{t('admin_all_statuses')}</option>
					{STATUS_KEYS.map((value) => (
						<option key={value} value={value}>
							{statusLabel[value]}
						</option>
					))}
				</select>
			</div>
			<div className="admin_records">
				{filteredRecords.map((record) => (
					<section className="admin_record" key={record.key}>
						<div className="admin_record_head">
							<strong>{getSchoolName(record)}</strong>
							<span className={`admin_status ${record.status || 'submitted'}`}>
								{statusLabel[record.status] || record.status || t('admin_status_submitted')}
							</span>
						</div>
						<div className="admin_record_grid">
							<span>{t('admin_field_amount')}</span>
							<strong>{record.amountTon} TON</strong>
							<span>{t('admin_field_submitted')}</span>
							<strong>{formatDate(record.submittedAt)}</strong>
							<span>{t('admin_field_recorded')}</span>
							<strong>{formatDate(record.recordedAt)}</strong>
							<span>{t('admin_field_wallet')}</span>
							<strong className="admin_hash">{record.walletAddress || '-'}</strong>
							<span>{t('admin_field_message')}</span>
							<strong className="admin_hash">{record.signedBocHash || '-'}</strong>
							<span>{t('admin_field_transaction')}</span>
							<strong className="admin_hash">
								{record.confirmation?.transactionHash || record.confirmation?.reason || '-'}
							</strong>
						</div>
						<div className="admin_record_actions">
							<Button size="mini" onClick={() => copyValue(record.signedBocHash || record.walletAddress)}>
								{t('admin_copy_hash')}
							</Button>
							<Button size="mini" onClick={() => confirmRecords(record.key)}>
								{t('admin_check_onchain')}
							</Button>
						</div>
					</section>
				))}
				{!filteredRecords.length && !loading && <p className="admin_empty">{t('admin_empty')}</p>}
			</div>
		</div>
	);
};

export default Admin;
export { readStoredToken, persistToken, removeTokenFromUrl, TOKEN_STORAGE_KEY };
